<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onBeforeMount, onMounted, toRef, watch } from 'vue'
import { useHead } from '@unhead/vue'

import { AssetType } from '@xcel-next/core/enums'

import { useSourcepointStore } from '../stores/sourcepoint'
import { useSessionStore } from '../stores/session'
import { useConfigStore } from '../stores/config'
import { useAdaStore } from '../stores/ada'
import { usePageStore } from '../stores/page'
import { useUrlHelper } from '../composables/use-url-helper'
import { useCustomDimensions } from '../composables/use-custom-dimensions'
import { useMeta } from '../composables/use-meta'
import { useClient } from '../utils/client-only'
import AdaAdSlot from './ad-slots/AdaAdSlot.vue'
import AdaNativeCrossbar from './ad-slots/AdaNativeCrossbar.vue'
import AdTaboola from './ad-slots/AdTaboola.vue'
import PageContent from './PageContent.vue'
import PageHeader from './PageHeader.vue'
import PreviewTimer from './PreviewTimer.vue'
import Notify from './shared/Notify.vue'
import GoogleTagManager from './tracking/GoogleTagManager.vue'
import Agf from './tracking/Agf.vue'
import VgWort from './tracking/VgWort.vue'
import PageContentVideoOnly from './PageContentVideoOnly.vue'

import type { Ref } from 'vue'
import type { Brand } from '@xcel-next/core/enums'
import type { ComposedPage } from '@xcel-next/composer-schema'
import type { Taboola } from '@xcel-next/rendering-tools'
import type { ReactiveHead } from '@unhead/vue'

declare global {
  interface Window {
    __tcfapi?: (command: string, version: number, callback: (data: unknown, success: boolean) => void, ...args: unknown[]) => void
  }
}

const props = defineProps<{
  pageData?: ComposedPage,
  brand?: Brand
}>()

const sourcepointStore = useSourcepointStore()
const adaStore = useAdaStore()
const pageStore = usePageStore()
const { isClient } = useClient()

const { getQueryParams } = useUrlHelper()
// pageData can briefly be undefined before the page store loads it - useCustomDimensions/useMeta
// already guard every access with `pageData.value?.x` internally, so this cast is safe
useCustomDimensions(props.brand ?? ('' as Brand), toRef(props, 'pageData') as Ref<ComposedPage>)
const { generateMetas } = useMeta(toRef(props, 'pageData') as Ref<ComposedPage>, computed(() => props.brand ?? ''))

useHead(computed(() => generateMetas() as unknown as ReactiveHead))

const { hasPurSub } = storeToRefs(useSessionStore())
const { rsConfig } = storeToRefs(useConfigStore())
const { isForceTestZone, dynamicAdSlotsEnabled } = storeToRefs(adaStore)
const { isArticleOrArticleVersion, isPrintPreview } = storeToRefs(pageStore)

const resolvedPageContentComponent = computed(() => {
  return props.pageData?.metaData?.isVideoOnlyArticle ? PageContentVideoOnly : PageContent
})

const pageTypeStyleClass = computed(() => {
  // convert page type name to css class name
  const pageTypeClass =
    props.pageData?.pageMeta?.pageType && !props.pageData.metaData?.behaveLikeArticle
      ? 'bx-page--' + props.pageData.pageMeta.pageType
      : 'bx-page--article'
  const articleTypeClass =
    props.pageData?.metaData?.isVideoOnlyArticle
      ? 'bx-article--video-only'
      : props.pageData?.pageMeta?.articleType
        ? 'bx-article--' + props.pageData.pageMeta.articleType
        : ''
  const dsTableModifierClass = // Datenschutz-Tabellen modifier
    props.pageData?.pageMeta?.pageType === 'structureterm' && props.pageData?.pageMeta?.url?.match(/\/datenschutz/i)
      ? 'bx-page--datenschutz'
      : ''
  return {
    'bx-grid': true,
    [pageTypeClass]: true,
    [articleTypeClass]: true,
    [dsTableModifierClass]: dsTableModifierClass.length > 0
  }
})

const isNotAdvertorial = computed(() => {
  return !props.pageData?.metaData?.advertorial
})

const allowNativeTeasers = computed(() => {
  return !props.pageData?.metaData?.disallowNativeTeasers
})

const taboolaConfig = computed<Taboola>(() => {
  if (typeof window === 'undefined') return {} as Taboola

  const configs = rsConfig.value?.adSlotUsage?.taboola ?? []
  const currentUrl = window.location.pathname || ''

  // try to find the first config matching current url by either exact match or prefix wildcard
  const configForUrl = configs.find(({ urls }) => urls?.some((url) => {
    if (url.endsWith('/*')) {
      const prefix = url.replace('/*', '')
      return currentUrl.startsWith(prefix) // prefix wildcard match
    }
    return currentUrl === url // exact match
  }))

  if (configForUrl) {
    return configForUrl
  }

  return configs.find((config) => config.isDefault) ?? {} as Taboola
})

const taboolaEnabled = computed(() => {
  return !hasPurSub.value && !props.pageData?.metaData?.advertorial && !props.pageData?.metaData?.disallowTaboola ? taboolaConfig.value.enabled : false
})

const taboolaBeforeSeoText = computed(() => {
  const pageMeta = props.pageData?.pageMeta
  if (!pageMeta?.pageId) return false

  return pageMeta.pageType === AssetType.StructureTerm &&
    !pageMeta.isTransformedAsset &&
    !!props.pageData?.body?.seoText &&
    !props.pageData?.metaData?.isVideoOnlyArticle &&
    (isArticleOrArticleVersion.value || !!props.pageData?.metaData?.behaveLikeArticle) &&
    taboolaConfig.value.beforeSeoTextPageIds?.includes(pageMeta.pageId) === true
})

const setBrandPageStyleClass = () => {
  if (props.pageData?.metaData?.advertorial) {
    document.getElementsByTagName('body')[0].classList.add('bx-page-style--advertorial')
  }
}

const specialAdPrintStyle = (isPrintPreviewValue: boolean) => {
  const specialAds = Array.from(document.querySelectorAll<HTMLElement>('[id]')).filter(el => /(superbanner|leaderboard)/.test(el.id))

  if (isPrintPreviewValue) {
    specialAds.forEach(el => {
      el.style.display = 'none'
    })
  } else {
    specialAds.forEach(el => {
      el.removeAttribute('style')
    })
  }
}

const loadSvgSprite = async (url: string) => {
  try {
    // check if the sprite has already been loaded
    if (document.querySelector(`div[data-svg-sprite="${url}"]`)) {
      return
    }

    const res = await fetch(url)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)

    const spriteText = await res.text()

    const container = document.createElement('div')
    container.style.display = 'none'
    container.innerHTML = spriteText
    container.setAttribute('data-svg-sprite', url) // markiere als geladen

    const svg = container.querySelector('svg')
    if (svg) {
      svg.setAttribute('aria-hidden', 'true')
      svg.setAttribute('focusable', 'false')
    }

    document.body.prepend(container)
  } catch (err) {
    console.error('Failed to load SVG sprite:', err)
  }
}

watch(isPrintPreview, (newValue) => {
  specialAdPrintStyle(newValue)
})

onBeforeMount(async () => {
  // Load SVG icon sprites from CDN paths defined in config and embed them into the document body
  // We cannot reference them in <use> directly, because browsers block loading from cdns due to CORS policy
  rsConfig.value.cdnIconSpritePaths?.forEach(async (spritePath) => {
    await loadSvgSprite(`${rsConfig.value.cdnUri}${spritePath}?v=${process.env.RS_VERSION}`)
  })

  const forceTestZone = getQueryParams()?.ftz
  if (forceTestZone) {
    adaStore.setTestZone(forceTestZone)
  } else {
    adaStore.checkForceTestZoneCookie()
  }
  if (getQueryParams()?.ada === '1') {
    adaStore.forcePreviewBundle()
  } else if (isForceTestZone.value) {
    // apply testZone=develop to URL - not loosing existing query params
    const urlParams = new URLSearchParams(window.location.search)
    if (!urlParams.has('testZone')) {
      urlParams.set('testZone', 'develop')
    }
    history.pushState(null, '', `?${urlParams.toString()}`)
  }
})

onMounted(() => {
  sourcepointStore.refreshCustomVendorConsents()
  setBrandPageStyleClass()
  window.__tcfapi?.('addEventListener', 2, (tcData, success) => {
    if (success && (tcData as { eventStatus?: string })?.eventStatus === 'useractioncomplete') {
      sourcepointStore.consentActionPerformed()
    }
  })
})
</script>

<template>
  <div v-if="pageData"
       class="bx-typo bx-page-wrapper">
    <Notify />
    <GoogleTagManager />
    <Agf :page-data="pageData"
         :brand="brand" />
    <VgWort v-if="rsConfig.vgWort?.enabled && !pageData.pageMeta?.isTransformedAsset /* we don't want no vgwort tracking for 'fake' articles (e.g. originally epg-broadcast or vod assets) */"
            :page-meta="pageData.pageMeta" />
    <PreviewTimer v-if="pageData?.pageMeta?.previewToken"
                  :preview-token="pageData.pageMeta.previewToken" />
    <PageHeader v-if="pageData && pageData.header"
                :header-data="pageData.header"
                :brand="brand">
      <!-- eslint-disable vue/valid-v-slot -->
      <template v-for="(_, slot) of $slots"
                #[slot]="scope">
        <slot :name="slot"
              v-bind="scope" />
      </template>
      <!-- eslint-enable vue/valid-v-slot -->
    </PageHeader>
    <div id="bx-grid-wrapper"
         class="bx-grid-wrapper bx-normalize">
      <div :class="pageTypeStyleClass">
        <div v-if="!pageData?.metaData?.isVideoOnlyArticle"
             class="bx-ada bx-ada--top-slots bx-ada--superbanner_1-wallpaper_1">
          <AdaAdSlot slot-name="wallpaper"
                     :slot-id="1" />
          <AdaAdSlot slot-name="superbanner"
                     :slot-id="1" />
        </div>
        <AdaAdSlot v-if="!dynamicAdSlotsEnabled"
                   slot-name="mobile"
                   :slot-id="1" />
        <AdaAdSlot slot-name="skyscraper"
                   :slot-id="1" />
        <AdaAdSlot slot-name="skyscraper"
                   :slot-id="2" />

        <component :is="resolvedPageContentComponent"
                   v-if="pageData?.pageMeta && pageData?.metaData && pageData?.body"
                   :header-data="pageData.header"
                   :page-meta="pageData.pageMeta"
                   :meta-data="pageData.metaData"
                   :asset="pageData.body"
                   :taboola-enabled="taboolaEnabled"
                   :brand="brand">
          <!-- eslint-disable vue/valid-v-slot -->
          <template v-for="(_, slot) of $slots"
                    #[slot]="scope">
            <slot :name="slot"
                  v-bind="scope" />
          </template>
          <!-- eslint-enable vue/valid-v-slot -->
          <template #before-seo-text>
            <AdTaboola v-if="isClient && taboolaEnabled && taboolaBeforeSeoText"
                       :taboola-config="taboolaConfig" />
          </template>
        </component>

        <div v-if="isArticleOrArticleVersion || pageData?.metaData?.behaveLikeArticle"
             class="bx-grid--container">
          <div :class="{ 'bx-collection': !dynamicAdSlotsEnabled }">
            <div :class="{ 'bx-collection__inner-wrapper': !dynamicAdSlotsEnabled }">
              <AdTaboola v-if="taboolaEnabled && !taboolaBeforeSeoText"
                         :taboola-config="taboolaConfig" />
            </div>
          </div>
        </div>

        <div class="bx-grid--container">
          <AdaNativeCrossbar v-if="isNotAdvertorial && allowNativeTeasers && !pageData?.metaData?.isVideoOnlyArticle"
                             position="bottom" />
        </div>

        <AdaAdSlot slot-name="dmofooter"
                   :slot-id="1" />
        <AdaAdSlot slot-name="mobile"
                   :slot-id="10" />
      </div>
    </div>
    <slot v-if="pageData && pageData.footer"
          name="footer" />
  </div>
</template>
